// src/pages/PatientCareTeamPage.jsx
// Route: /patient/care-team
//
// Lists the patient's conversations with their doctor(s) and navigator(s),
// and renders the open thread inline. "+ Message" buttons let the patient
// start a new conversation themselves — picks from getEligibleCounterparts
// (doctors they have a qualifying appointment with; any navigator, since
// that pair is unrestricted today).

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { PatientAppShell } from '../components/PatientLayout';
import { usePatient } from '../context/PatientContext';
import { useToast } from '../context/ToastContext';
import { chatMessagingAPI } from '../services/chatMessagingAPI';
import { careTeamAPI } from '../services/careTeamAPI';
import { useHumanChat } from '../hooks/useHumanChat';
import HumanChatWindow from '../components/chat/HumanChatWindow';
import NewConversationPicker from '../components/chat/NewConversationPicker';
import avatarTint, { avatarAccentFill } from '../utils/avatarTint';
import EmptyState from '../components/EmptyState';
import { requestMarkNotificationsRead } from '../utils/notificationReadState';

function initials(name) {
  if (!name) return '?';
  return name.trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();
}

/**
 * The strip's card label is meant to read as a first name — "Rakesh",
 * "Sanesh" — for a navigator. A doctor's `name` starts with a "Dr"/"Dr."
 * title, so the same `name.split(' ')[0]` that works for a navigator only
 * ever returns the title itself ("Dr.") and drops the actual name. Doctors
 * get the title plus their first real name instead — "Dr. Priya" — so the
 * card still reads as someone's name, not just their role repeated.
 */
function displayFirstName(name, role) {
  if (!name) return '';
  const parts = name.trim().split(/\s+/);
  if (role === 'DOCTOR' && /^dr\.?$/i.test(parts[0])) {
    return parts.length > 1 ? `Dr. ${parts[1]}` : 'Dr.';
  }
  return parts[0];
}

export default function PatientCareTeamPage() {
  const { patient } = usePatient();
  const { toast } = useToast();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [pickerType, setPickerType] = useState(null); // 'PATIENT_DOCTOR' | 'PATIENT_NAVIGATOR' | null (closed)
  const [filter, setFilter] = useState('ALL'); // 'ALL' | 'DOCTOR' | 'NAVIGATOR' — the strip's own filter, doesn't affect selection/loading
  const [searchParams, setSearchParams] = useSearchParams();
  const wantsNavigator = searchParams.get('navigator') === '1';

  // DEF-018 / DEF-019 / DEF-032 — the care team is now loaded from
  // GET /api/patients/{id}/care-team, not from the conversation list.
  //
  // This page used to render chatMessagingAPI.list() directly, which defined the
  // care team as "whoever I happen to have a chat thread with". Three defects
  // came out of that:
  //   DEF-018 — a doctor showed with role "Navigator", because the role rendered
  //             was the CONVERSATION's role, a property of the thread rather
  //             than of the person.
  //   DEF-019 — a doctor with a real appointment but no thread yet was missing
  //             entirely: no thread meant no row to render.
  //   DEF-032 — the same confusion from the other side.
  //
  // The care team is a clinical fact — the assigned navigator plus the doctors
  // the patient has an appointment with — and it exists whether or not anyone has
  // sent a message. Conversation state (unread count, last activity) is merged
  // ONTO each member, which is the correct direction: a thread is a property of a
  // relationship, not the other way round.
  const loadCareTeam = useCallback(() => {
    if (!patient?.id) return;
    setLoading(true);
    Promise.allSettled([
      careTeamAPI.list(patient.id),
      chatMessagingAPI.list(),
    ])
      .then(([teamRes, convRes]) => {
        if (teamRes.status === 'rejected') {
          setError(teamRes.reason?.message || 'Could not load your care team.');
          return;
        }
        const team = Array.isArray(teamRes.value) ? teamRes.value : [];
        const threads = convRes.status === 'fulfilled' && Array.isArray(convRes.value)
          ? convRes.value
          : [];
        const threadById = new Map(threads.map(t => [t.id, t]));

        const teamMembers = team.map(m => {
          const thread = m.conversationId ? threadById.get(m.conversationId) : null;
          return {
            ...m,
            // conversationId may be null — that is "no thread yet", not an error.
            unreadCount:   thread?.unreadCount || 0,
            lastMessageAt: thread?.lastMessageAt || null,
          };
        });

        // "Message a navigator" offers EVERY navigator, not just the one
        // assigned to this patient (that pair is unrestricted — see
        // NewConversationPicker's header comment) — so a PATIENT_NAVIGATOR
        // thread can exist with someone the care team list doesn't cover.
        // Without this, such a thread has nowhere to attach: `active` only
        // ever matches by care-team membership, so the pane sat on
        // "No conversation open" forever even though the conversation was
        // created successfully server-side. Surface it as its own row.
        const coveredConversationIds = new Set(
          teamMembers.map(m => m.conversationId).filter(Boolean)
        );
        const extraMembers = threads
          .filter(t => (
            (t.conversationType === 'PATIENT_DOCTOR' || t.conversationType === 'PATIENT_NAVIGATOR')
            && !coveredConversationIds.has(t.id)
          ))
          .map(t => ({
            id: t.counterpartId,
            name: t.counterpartName,
            role: t.counterpartRole,
            specialty: null,
            conversationId: t.id,
            unreadCount: t.unreadCount || 0,
            lastMessageAt: t.lastMessageAt || null,
          }));

        setMembers(prev => {
          const knownConversationIds = new Set([
            ...teamMembers.map(m => m.conversationId).filter(Boolean),
            ...extraMembers.map(m => m.conversationId),
          ]);
          // A conversation just created locally (e.g. via "Message a
          // navigator") has zero messages, and listConversationsForPatient()
          // deliberately excludes zero-message threads (DEF-033) — so it is
          // absent from `threads` above until the first message is sent.
          // Without this, the very refetch meant to merge it in instead wipes
          // out the optimistic row attachConversation() just added: the pane
          // the patient just opened snaps back to "No conversation open" even
          // though the conversation exists server-side and is already marked
          // active. Keep any locally-known member the fresh fetch doesn't
          // cover yet — it naturally rejoins via extraMembers once a message
          // is sent and the thread starts showing up in the real list.
          const stillPending = prev.filter(m => (
            m.conversationId && !knownConversationIds.has(m.conversationId)
          ));
          return [...teamMembers, ...extraMembers, ...stillPending];
        });
        setError(null);
      })
      .finally(() => setLoading(false));
  }, [patient?.id]);

  useEffect(() => { loadCareTeam(); }, [loadCareTeam]);

  // autoSelectedRef also guards the deep-link effect right below — both are
  // "pick the initial conversation" logic, and only one may win.
  const autoSelectedRef = useRef(false);

  // Deep-link from the notification bell (?conversationId=...) — see
  // useNotifications.js. Runs before the auto-select-most-recent effect
  // below and marks autoSelectedRef so that effect never overrides it: a
  // notification names a SPECIFIC thread, and without this the bell's "to"
  // link could only ever point at this page in general, so clicking any
  // unread-message notification just opened whatever conversation happened
  // to be most recently active — not the one the notification was about.
  useEffect(() => {
    const cid = searchParams.get('conversationId');
    if (!cid) return;
    setActiveId(cid);
    autoSelectedRef.current = true;
  }, [searchParams]);

  // Auto-select the most recently active conversation once the list loads
  // — otherwise the right-hand panel sits on "Select a conversation to view
  // messages" even when there's an obvious most-recent thread to show.
  // Picks explicitly by lastMessageAt (falling back to createdAt for a
  // thread with no messages yet) rather than trusting the list's own order
  // to stay sorted that way. Only fires when nothing is selected yet, so it
  // won't yank the view away from whatever the patient is actively reading
  // on a later re-fetch.
  // 18 Jul 2026 fix: this used to re-run and re-select the same conversation
  // every time activeId became null — including when the mobile "← Back"
  // button explicitly set it to null to return to the list. Invisible on
  // desktop (which never sets activeId to null in normal use — clicking a
  // different thread goes straight from one id to another), but on mobile
  // it meant Back appeared to do nothing: it snapped straight back to the
  // same conversation before the list was ever visible. autoSelectedRef
  // makes this fire only once, on initial load — never again afterward,
  // regardless of how many times the patient explicitly backs out.
  useEffect(() => {
    if (autoSelectedRef.current) return;
    if (activeId) return;
    // The ?navigator=1 effect below decides first; it removes the parameter
    // once done, and if it found no navigator this runs then instead.
    if (wantsNavigator) return;
    // Only members who already have a thread are auto-selectable — there is
    // nothing to open for a member the patient has never messaged, and opening
    // one on their behalf would create the never-initiated conversations DEF-033
    // was about.
    const withThreads = members.filter(m => m.conversationId);
    if (withThreads.length === 0) return;
    const mostRecent = withThreads.reduce((latest, m) => (
      new Date(m.lastMessageAt || 0).getTime() > new Date(latest.lastMessageAt || 0).getTime()
        ? m : latest
    ));
    setActiveId(mostRecent.conversationId);
    autoSelectedRef.current = true;
  }, [members, activeId, wantsNavigator]);

  // `activeId ?` is load-bearing, not defensive noise. A care-team member with
  // no thread yet has conversationId === null (DEF-019 — the member exists as a
  // clinical fact whether or not anyone has messaged), and activeId is null when
  // nothing is selected. So a bare find() matched null === null and returned a
  // member the patient had never opened: the right-hand pane rendered a chat
  // window for them, headed with their name, stuck on "Loading conversation…"
  // (useHumanChat(null) never clears its initial loading state) with a live-
  // looking message box that silently dropped everything typed into it —
  // sendMessage returns early without a conversationId, so not one request left
  // the browser. A brand-new patient could not chat at all, and got no error
  // saying so.
  //
  // Only this page is exposed: the three sibling inboxes match on c.id, a
  // conversation's own id, which is never null.
  const active = activeId ? (members.find(m => m.conversationId === activeId) || null) : null;
  const chat = useHumanChat(activeId);

  // Opening a conversation reads it: the server resets the thread's unread
  // count (useHumanChat), and the same moment clears that member's badge here
  // and their entry in the notification bell — otherwise both would keep
  // announcing messages the patient is looking at.
  useEffect(() => {
    if (!activeId) return;
    setMembers(prev => (
      prev.some(m => m.conversationId === activeId && m.unreadCount > 0)
        ? prev.map(m => (m.conversationId === activeId ? { ...m, unreadCount: 0 } : m))
        : prev
    ));
    requestMarkNotificationsRead([`msg-${activeId}`]);
  }, [activeId]);

  /**
   * Records a freshly created thread against the member it belongs to.
   *
   * `counterpart` (optional, `{ id, name, role }`) is only used when
   * `memberId` isn't already in `members` — e.g. any navigator can be
   * messaged via "Message a navigator", not just the assigned one, so the
   * picked counterpart may not be a care-team member at all. Without adding
   * a row for them here, `active` (which only ever matches by care-team
   * membership) can't resolve this conversation until the loadCareTeam()
   * refetch below lands and merges it in — this just removes that gap.
   */
  function attachConversation(memberId, conversationId, counterpart) {
    setMembers(prev => {
      if (prev.some(m => m.id === memberId)) {
        return prev.map(m => (m.id === memberId ? { ...m, conversationId } : m));
      }
      if (!counterpart) return prev;
      return [...prev, {
        id: memberId,
        name: counterpart.name,
        role: counterpart.role,
        specialty: null,
        conversationId,
        unreadCount: 0,
        lastMessageAt: null,
      }];
    });
  }

  /**
   * Opens a member's thread, creating it on first use.
   *
   * A member with no conversationId is normal (DEF-019) — the thread is created
   * here, when the patient actually chooses to message them, rather than being
   * pre-created for everyone (which is what left patients with inbox entries they
   * had never started — DEF-033).
   */
  async function openMemberThread(member) {
    if (member.conversationId) {
      setActiveId(member.conversationId);
      return;
    }
    try {
      const conversation = await chatMessagingAPI.createOrGet(
        member.conversationType, member.id,
      );
      // Attach the new thread to this member locally BEFORE re-fetching.
      // `active` resolves by matching activeId against a member's
      // conversationId, so until loadCareTeam() returns a member carrying it
      // there is nothing for the new id to match — and the pane the patient
      // just opened falls back to the empty state. Whether that is visible at
      // all depends on how quickly the care-team fetch answers, which is
      // exactly the kind of thing that works on a fast connection and flickers
      // on a slow one. The re-fetch below is still authoritative; this only
      // removes the gap.
      attachConversation(member.id, conversation.id);
      setActiveId(conversation.id);
      loadCareTeam();
    } catch (e) {
      toast.error(e.message || 'Could not start that conversation.');
    }
  }

  // The sidebar's "Your navigator" card links here with ?navigator=1 — open
  // the navigator's thread (creating it on first use, like clicking their
  // card) instead of the most recent conversation. On first load it marks
  // autoSelectedRef, as the ?conversationId= deep link does, so the most-recent
  // default never overrides it; a ?conversationId= in the same URL still wins.
  // It also applies when the page is already open — the patient is on Care
  // Team and clicks the card — since that is a fresh request for the
  // navigator, not a re-run of the initial selection. Each navigation is
  // handled once (handledNavigatorKeyRef), and the parameter is then dropped
  // from the URL (replacing the history entry), so a refresh doesn't re-open
  // the navigator over whatever the patient has picked since.
  const location = useLocation();
  const handledNavigatorKeyRef = useRef(null);
  useEffect(() => {
    if (!wantsNavigator) return;
    // members is set in one go when the care team loads — wait for it.
    if (loading && members.length === 0) return;
    if (handledNavigatorKeyRef.current === location.key) return;
    handledNavigatorKeyRef.current = location.key;
    if (!searchParams.get('conversationId')) {
      const navigator = members.find(m => m.role === 'NAVIGATOR');
      if (navigator) {
        autoSelectedRef.current = true;
        setFilter('NAVIGATOR');
        openMemberThread(navigator);
      }
    }
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('navigator');
      return next;
    }, { replace: true });
    // openMemberThread is redeclared every render; handledNavigatorKeyRef
    // (and the parameter being removed) keeps this to one call per click.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantsNavigator, loading, members, searchParams, location.key]);

  async function handlePickCounterpart(counterpart) {
    try {
      const conversation = await chatMessagingAPI.createOrGet(pickerType, counterpart.id);
      setPickerType(null);
      // Same gap as above, but here it's the common case rather than the
      // exception: the picker offers any navigator, not only the assigned
      // one, so `counterpart` is very often not an existing care-team
      // member. Pass it through so attachConversation can add a row for
      // them immediately instead of relying solely on loadCareTeam()
      // below to merge them in once its refetch lands.
      attachConversation(counterpart.id, conversation.id, counterpart);
      setActiveId(conversation.id);
      loadCareTeam();
    } catch (e) {
      toast.error(e.message || 'Could not start that conversation.');
    }
  }

  const filteredMembers = members.filter(m => filter === 'ALL' || m.role === filter);

  /**
   * Switching the All/Doctors/Navigators filter opens the new list's first
   * card, the same way clicking it would — so a patient landing on, say, the
   * Doctors tab always sees a thread already loaded instead of the empty
   * "pick someone" state, even when the tab they were on before had a
   * different member (or none) selected.
   */
  function selectFilter(value) {
    setFilter(value);
    const list = members.filter(m => value === 'ALL' || m.role === value);
    if (list.length > 0) openMemberThread(list[0]);
  }

  return (
    <PatientAppShell breadcrumbs={[{ label: 'Care Team' }]}>
      <div className="careteam-page">
        <div className="page-header careteam-header">
          <div>
            <h1 className="page-title">Care team</h1>
            <div className="page-subtitle">Your dedicated health support</div>
          </div>
        </div>
        <p className="careteam-hint">Select a member of your care team to start a secure conversation.</p>

        {/* Member panel. A filterable single row of cards that scrolls
            sideways once the team outgrows it (4 across on desktop, 2 on
            tablet, one and a peek on a phone), so the thread below never
            loses height to a large team. */}
        <div className="panel careteam-panel">
          <div className="careteam-panel-head">
            <div className="careteam-panel-title">
              Your care team
              <span className="careteam-panel-count">
                {members.length} member{members.length === 1 ? '' : 's'}
              </span>
            </div>

            <div className="tabs-bar careteam-filter" role="tablist">
              {[['ALL', 'All'], ['DOCTOR', 'Doctors'], ['NAVIGATOR', 'Navigators']].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  role="tab"
                  aria-selected={filter === value}
                  className={`tab-btn${filter === value ? ' active' : ''}`}
                  onClick={() => selectFilter(value)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {loading && members.length === 0 && (
            <div className="careteam-note">Loading…</div>
          )}
          {error && (
            <div className="careteam-note careteam-note--error">{error}</div>
          )}
          {!loading && !error && members.length === 0 && (
            <EmptyState
              compact
              icon="people"
              title="No care team yet"
              message="Your navigator appears here once assigned, and your doctor once you
                       have an appointment."
            />
          )}
          {!loading && !error && members.length > 0 && filteredMembers.length === 0 && (
            <div className="careteam-note">
              No {filter === 'DOCTOR' ? 'doctors' : 'navigators'} on your care team yet.
            </div>
          )}

          <div className="careteam-grid">
            {filteredMembers.map((m) => {
              const isActive = m.conversationId && m.conversationId === activeId;
              const accent = avatarAccentFill(m.name);
              const roleLabel = m.role === 'DOCTOR' ? 'Doctor' : 'Navigator';
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => openMemberThread(m)}
                  aria-pressed={!!isActive}
                  title={`${m.name} — ${roleLabel}${m.specialty ? ` · ${m.specialty}` : ''}`}
                  className={`careteam-card${isActive ? ' active' : ''}`}
                  style={{ '--member-accent': accent }}
                >
                  <div className={`avatar ${avatarTint(m.name)} careteam-card-avatar`} aria-hidden="true">
                    {initials(m.name)}
                  </div>
                  <div className="careteam-card-body">
                    <div className="careteam-card-name">{displayFirstName(m.name, m.role)}</div>
                    {/* DEF-018: role comes from the MEMBER, not the conversation
                        — see loadCareTeam()'s header comment. */}
                    <div className="careteam-card-role">{m.specialty || roleLabel}</div>
                  </div>
                  {m.unreadCount > 0 && (
                    <span className="careteam-card-badge"
                      aria-label={`${m.unreadCount} unread message${m.unreadCount === 1 ? '' : 's'}`}>
                      {m.unreadCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Active thread */}
        <div style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
          {active ? (
            <HumanChatWindow
              counterpartName={active.name}
              counterpartRole={active.role}
              counterpartId={active.id}
              messages={chat.messages}
              loading={chat.loading}
              error={chat.error}
              sending={chat.sending}
              onSend={chat.sendMessage}
            />
          ) : (
            <div className="panel" style={{
              height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {/* Unframed: the wrapper above is already the surface. Was one
                  line of muted 13px text — the last bare "nothing here" string
                  on a new patient's screens. */}
              <EmptyState
                icon="people"
                title="No conversation open"
                message="Pick someone from your care team above to read the thread and reply."
              />
            </div>
          )}
        </div>
      </div>

      {pickerType && (
        <NewConversationPicker
          conversationType={pickerType}
          title={pickerType === 'PATIENT_DOCTOR' ? 'Message a doctor' : 'Message a navigator'}
          onSelect={handlePickCounterpart}
          onClose={() => setPickerType(null)}
        />
      )}
    </PatientAppShell>
  );
}
